import React, { useContext, useMemo } from 'react';

import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { StyleSheet, View } from 'react-native';

import PrimaryActionButton, { ActionButtonSizes } from 'components/action-button';
import { StoreLocationMap } from 'components/store-location-map';
import { IMarker } from 'components/store-location-map/types';
import { IconStyled } from 'components/top-service-mode/styled';
import { ActionButton as OutlineActionButton } from 'components/ui/action-button';
import { Typography } from 'components/ui/typography';
import { MarkerTypes } from 'hooks/use-map/types';
import { getMarkerTypeForAlias } from 'hooks/use-map/utils';
import { AmplitudeCheckoutSection, getCheckoutChangeButtonEventAttributes } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { SmartAlertsContext } from 'state/smart-alert';
import { primitive } from 'styles/constants';

import { useCheckoutLoadingState, useStoreLocatorWithMap } from '../../hooks';
import { WidgetBaseStyles } from '../../pages/styled';
import { ChangeStoreConfirmationDialog } from '../change-store-confirmation-dialog';

import { CloserRestaurantCoachmark } from './closer-restaurant-coachmark';
import { useCloserRestaurantCoachmark } from './use-closer-restaurant-coachmark';

const RESTAURANT_CLOSED_CHECKOUT_MESSAGE = 'Restaurant closed. Please, change restaurant.';

export const StoreLocatorWithMapWidget = () => {
  const {
    addressLine1,
    cityStateZip,
    isLoadingDistance,
    distanceText,
    canShowDistance,
    restaurantCoordinates,
    handleChangeStore,
    dialogProps,
  } = useStoreLocatorWithMap();
  const { isCheckoutLoading } = useCheckoutLoadingState();
  const {
    isVisible: isCloserRestaurantCoachmarkVisible,
    dismiss: dismissCloserRestaurantCoachmark,
    distanceText: closerRestaurantDistanceText,
  } = useCloserRestaurantCoachmark();

  const { deliveryAddress } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const { store } = useStoreContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();

  const { currentStoreDate, tsmSmartAlert } = useContext(SmartAlertsContext);
  const hasClosedRestaurantAlert =
    !!serviceMode && !isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime);
  const smartAlertMessage =
    hasClosedRestaurantAlert && !isDelivery
      ? RESTAURANT_CLOSED_CHECKOUT_MESSAGE
      : tsmSmartAlert?.message;
  const smartAlertSentenceEnd = smartAlertMessage?.indexOf('.') ?? -1;
  const smartAlertBoldPrefix =
    hasClosedRestaurantAlert && smartAlertSentenceEnd > -1
      ? smartAlertMessage?.slice(0, smartAlertSentenceEnd + 1)
      : null;
  const smartAlertMessageRest = smartAlertBoldPrefix
    ? smartAlertMessage?.slice(smartAlertBoldPrefix.length)
    : smartAlertMessage;
  const addressAccessibilityLabel = [
    addressLine1,
    cityStateZip,
    !isLoadingDistance && distanceText && canShowDistance ? distanceText : null,
  ]
    .filter(Boolean)
    .join(', ');

  const markers = useMemo(() => {
    const markersList: IMarker[] = [];

    if (isDelivery && deliveryAddress?.latitude && deliveryAddress?.longitude) {
      const deliveryMarkerType = getMarkerTypeForAlias(deliveryAddress?.alias);
      markersList.push({
        id: 'delivery',
        type: deliveryMarkerType,
        location: {
          lat: deliveryAddress.latitude,
          lng: deliveryAddress.longitude,
        },
      });
    }

    return markersList;
  }, [isDelivery, deliveryAddress]);

  return (
    <View>
      <View style={styles.mapContainer}>
        {restaurantCoordinates && (
          <StoreLocationMap
            storeLocation={restaurantCoordinates}
            title={addressLine1}
            storeMarkerType={
              hasClosedRestaurantAlert
                ? MarkerTypes.StoreNotAvailableSelected
                : MarkerTypes.StoreOpenSelected
            }
            markers={markers}
            showUserMarker={!isDelivery}
          />
        )}
      </View>
      {smartAlertMessage && (
        <View style={styles.smartAlertContainer}>
          <IconStyled variant="warning" />
          <Typography style={styles.smartAlertText}>
            {smartAlertBoldPrefix ? (
              <>
                <Typography style={styles.smartAlertTextBold}>{smartAlertBoldPrefix}</Typography>
                {smartAlertMessageRest}
              </>
            ) : (
              smartAlertMessage
            )}
          </Typography>
        </View>
      )}
      <View
        style={[
          styles.container,
          isCloserRestaurantCoachmarkVisible ? styles.coachmarkAnchor : null,
        ]}
      >
        <View
          accessible
          focusable
          accessibilityLabel={addressAccessibilityLabel}
          style={styles.addressContainer}
        >
          <Typography focusable={false} style={styles.addressText}>
            {addressLine1}
          </Typography>
          <View style={styles.space4} />
          <Typography focusable={false} style={styles.addressText}>
            {cityStateZip}
            {!isLoadingDistance && distanceText && canShowDistance && (
              <Typography
                focusable={false}
                style={styles.distanceText}
              >{` | ${distanceText}`}</Typography>
            )}
          </Typography>
        </View>
        {hasClosedRestaurantAlert ? (
          <PrimaryActionButton
            onPress={handleChangeStore}
            testID="change-store-button"
            accessibilityLabel={t('changeStores')}
            disabled={isCheckoutLoading}
            size={ActionButtonSizes.SMALL}
            eventAttributes={getCheckoutChangeButtonEventAttributes(
              AmplitudeCheckoutSection.Restaurant
            )}
          >
            {t('change')}
          </PrimaryActionButton>
        ) : (
          <OutlineActionButton
            onPress={handleChangeStore}
            testID="change-store-button"
            accessibilityLabel={t('changeStore', 'Change Store')}
            disabled={isCheckoutLoading}
            eventAttributes={getCheckoutChangeButtonEventAttributes(
              AmplitudeCheckoutSection.Restaurant
            )}
          />
        )}
        {isCloserRestaurantCoachmarkVisible ? (
          <CloserRestaurantCoachmark
            onDismiss={dismissCloserRestaurantCoachmark}
            distanceText={closerRestaurantDistanceText}
          />
        ) : null}
      </View>
      <ChangeStoreConfirmationDialog {...dialogProps} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...WidgetBaseStyles.container,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  coachmarkAnchor: {
    // The coachmark positions itself with `position: absolute` against this container. RN's
    // Yoga layout always anchors absolute children to their immediate parent regardless of
    // the parent's own position, but react-native-web maps this straight to CSS, where an
    // absolute element with no positioned ancestor escapes up to whichever ancestor (or the
    // map, or further) happens to have one — producing the misalignment seen on web.
    position: 'relative',
    zIndex: 10,
  },
  smartAlertContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    minHeight: 32,
    paddingVertical: 6,
    backgroundColor: primitive.$errorLight,
  },
  addressText: {
    fontSize: 14,
    lineHeight: 16,
    fontWeight: 'normal',
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
  },
  distanceText: {
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
  },
  smartAlertText: {
    flex: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
    fontSize: 14,
    lineHeight: 16,
    fontWeight: 'normal',
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
  },
  smartAlertTextBold: {
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
  },
  mapContainer: {
    minHeight: 160,
    flexDirection: 'row',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    overflow: 'hidden',
  },
  addressContainer: {
    flex: 1,
    paddingRight: 16,
  },
  space4: {
    marginTop: 4,
  },
});
